import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

export default function VCardDownloadPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading",
  );
  const [message, setMessage] = useState("Attempting to download vCard...");

  useEffect(() => {
    if (!id) {
      setStatus("error");
      setMessage("Invalid vCard ID provided.");
      return;
    }

    axios({
      url: `/api/v1/vcard/download/${id}`,
      method: "GET",
      responseType: "text",
    })
      .then((response) => {
        const vcardContent = response.data;
        const blob = new Blob([vcardContent], { type: "text/vcard" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "contact.vcf");
        document.body.appendChild(link);
        link.click();

        setStatus("success");
        setMessage("Download initiated. Please check your downloads folder.");

        // Clean up
        setTimeout(() => {
          URL.revokeObjectURL(url);
          document.body.removeChild(link);
        }, 100);
      })
      .catch((error) => {
        console.error("Error downloading vCard:", error);
        setStatus("error");
        if (error.response?.status === 404) {
          setMessage("This vCard link is expired or invalid.");
        } else {
          setMessage("An error occurred during the download.");
        }
      });
  }, [id]);

  const getStatusIcon = () => {
    switch (status) {
      case "loading":
        return <Loader2 className="h-6 w-6 animate-spin" />;
      case "success":
        return <CheckCircle2 className="h-6 w-6 text-success" />;
      case "error":
        return <AlertCircle className="h-6 w-6 text-destructive" />;
    }
  };

  return (
    <div className="container max-w-2xl py-8">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl font-bold">vCard Download</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <div className="flex items-center gap-2">
              {getStatusIcon()}
              <AlertTitle>
                {status === "loading"
                  ? "Processing..."
                  : status === "success"
                    ? "Success"
                    : "Error"}
              </AlertTitle>
            </div>
            <AlertDescription className="mt-2">{message}</AlertDescription>
          </Alert>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => navigate("/")}>
              Return to Home
            </Button>
            {status === "error" && (
              <Button onClick={() => window.location.reload()}>
                Try Again
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
